import { useMemo } from "react";

/**
 * DateOfBirthSelect
 * Three scroll-friendly native <select> dropdowns: Day · Month · Year.
 * Stores state as { day, month, year } strings (each "" when unset).
 *
 * Months are localized (EN / SR Latin). Year range: 1920 → currentYear.
 * On any change, calls onChange(nextValue) and also passes the ISO date
 * (or "") as the second argument so the parent can keep its own composite
 * field in sync without duplicating the assembly logic.
 *
 * Props:
 *   value     – { day, month, year }
 *   onChange  – (nextValue, isoDate) => void
 *   lang      – "en" | "sr"
 *   testidPrefix – e.g. "dob"  → produces "dob-day", "dob-month", "dob-year"
 */
const MONTHS_EN = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const MONTHS_SR = [
  "Januar", "Februar", "Mart", "April", "Maj", "Jun",
  "Jul", "Avgust", "Septembar", "Oktobar", "Novembar", "Decembar",
];

const pad2 = (n) => String(n).padStart(2, "0");

const computeIso = ({ day, month, year }) => {
  if (!day || !month || !year) return "";
  return `${year}-${pad2(month)}-${pad2(day)}`;
};

export function DateOfBirthSelect({
  value,
  onChange,
  lang = "en",
  testidPrefix = "dob",
  maxYear,
  minYear = 1920,
}) {
  const months = lang === "sr" ? MONTHS_SR : MONTHS_EN;
  const currentYear = new Date().getFullYear();
  const topYear = maxYear ?? currentYear;

  const years = useMemo(() => {
    const out = [];
    for (let y = topYear; y >= minYear; y--) out.push(y);
    return out;
  }, [topYear, minYear]);

  const daysInMonth = useMemo(() => {
    const m = parseInt(value.month, 10);
    const y = parseInt(value.year, 10) || currentYear;
    if (!m) return 31;
    return new Date(y, m, 0).getDate();
  }, [value.month, value.year, currentYear]);

  const days = useMemo(() => {
    const out = [];
    for (let d = 1; d <= daysInMonth; d++) out.push(d);
    return out;
  }, [daysInMonth]);

  const update = (patch) => {
    const next = { ...value, ...patch };
    // If selected day exceeds days in the new month, clamp/reset.
    if (next.day && next.month && next.year) {
      const cap = new Date(parseInt(next.year, 10), parseInt(next.month, 10), 0).getDate();
      if (parseInt(next.day, 10) > cap) next.day = "";
    } else if (next.day && next.month && !next.year) {
      // Use leap-safe cap (29 for Feb when year unknown).
      const cap = new Date(2020, parseInt(next.month, 10), 0).getDate();
      if (parseInt(next.day, 10) > cap) next.day = "";
    }
    onChange(next, computeIso(next));
  };

  const placeholder = {
    day: lang === "sr" ? "Dan" : "Day",
    month: lang === "sr" ? "Mesec" : "Month",
    year: lang === "sr" ? "Godina" : "Year",
  };

  const selectClass =
    "w-full px-3 py-3 rounded-xl bg-white border border-[#E8E7E2] focus:border-[#0A0A0F] focus:outline-none transition-colors text-sm text-[#0A0A0F] appearance-none cursor-pointer";

  return (
    <div className="grid grid-cols-3 gap-2">
      <select
        data-testid={`${testidPrefix}-day`}
        value={value.day}
        onChange={(e) => update({ day: e.target.value })}
        className={selectClass}
        aria-label={placeholder.day}
      >
        <option value="" disabled>{placeholder.day}</option>
        {days.map((d) => (
          <option key={d} value={String(d)}>{d}</option>
        ))}
      </select>
      <select
        data-testid={`${testidPrefix}-month`}
        value={value.month}
        onChange={(e) => update({ month: e.target.value })}
        className={selectClass}
        aria-label={placeholder.month}
      >
        <option value="" disabled>{placeholder.month}</option>
        {months.map((m, i) => (
          <option key={i + 1} value={String(i + 1)}>{m}</option>
        ))}
      </select>
      <select
        data-testid={`${testidPrefix}-year`}
        value={value.year}
        onChange={(e) => update({ year: e.target.value })}
        className={selectClass}
        aria-label={placeholder.year}
      >
        <option value="" disabled>{placeholder.year}</option>
        {years.map((y) => (
          <option key={y} value={String(y)}>{y}</option>
        ))}
      </select>
    </div>
  );
}
